import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { apiUtils } from '../hooks/useApi';
import { API_ENDPOINTS } from '../services/apiEndpoints';
import { Hotel } from '../hooks/useHotels';
import { useToast } from '../hooks/useToast';
import {
  Subscription,
  subscriptionService,
} from '../services/subscriptionService';
import { flushSync } from 'react-dom';

// Upload response types
interface UploadedFile {
  original_filename: string;
  attachment_id: string;
  preview_url: string;
}

// Travel facing page data types
export interface TravelFacingPage {
  mission_statement: string;
  brand_logo_attachments: Array<{
    attachment_id: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
  }>;
  marketing_gallery_attachments: Array<{
    attachment_id: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
  }>;
  ppp_gallery_attachments: Array<{
    attachment_id: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
  }>;
  promotional_video_attachments: Array<{
    attachment_id: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
  }>;
}

// ALO Score data (placeholder for future implementation)
export interface AloScore {
  score?: number;
  breakdown?: Record<string, any>;
}

// Context state interface
export interface HotelDetailContextState {
  // Hotel data
  hotel: Hotel | null;
  hotelLoading: boolean;
  hotelError: string | null;

  // Subscription data
  hotelSubscription: Subscription | null;
  subscriptionLoading: boolean;
  subscriptionError: string | null;
  showBoostUpgrade: boolean;
  isBoostHotel: boolean;

  // Travel facing page data
  travelFacingPage: TravelFacingPage | null;
  travelFacingLoading: boolean;
  travelFacingError: string | null;

  // ALO Score data (for future use)
  aloScore: AloScore | null;
  aloScoreLoading: boolean;
  aloScoreError: string | null;

  // Actions
  refetchHotel: () => Promise<void>;
  refetchHotelSilently: () => Promise<Hotel | null>;
  refetchSubscription: () => Promise<void>;
  refetchTravelFacing: () => Promise<void>;
  updateMissionStatement: (statement: string) => Promise<void>;
  uploadBrandLogo: (file: File) => Promise<void>;
  uploadMarketingGallery: (files: File[]) => Promise<void>;
  uploadPppGallery: (files: File[]) => Promise<void>;
  uploadPromotionalVideo: (file: File) => Promise<void>;
  deleteAttachment: (attachmentId: string) => Promise<void>;
  updateHotelStatus: (status: string) => void;
  updateHotelPermissions: (
    permissions: Partial<Hotel['userPermissions']>
  ) => void;

  // Loading states for uploads
  missionStatementLoading: boolean;
  brandLogoUploading: boolean;
  marketingGalleryUploading: boolean;
  pppGalleryUploading: boolean;
  promotionalVideoUploading: boolean;
  deletingAttachment: boolean;
}

// Create context
const HotelDetailContext = createContext<HotelDetailContextState | null>(null);

// Provider props
interface HotelDetailProviderProps {
  children: React.ReactNode;
  hotelId: string;
  skipInitialFetch?: boolean;
}

// Helper function to convert uploaded files to our format
const convertUploadedFiles = (uploadedFiles: UploadedFile[]) => {
  return uploadedFiles.map(file => {
    // Determine MIME type from file extension
    const extension = file.original_filename.toLowerCase().split('.').pop();
    let mimeType = 'application/octet-stream';

    switch (extension) {
      case 'png':
        mimeType = 'image/png';
        break;
      case 'jpg':
      case 'jpeg':
        mimeType = 'image/jpeg';
        break;
      case 'gif':
        mimeType = 'image/gif';
        break;
      case 'webp':
        mimeType = 'image/webp';
        break;
      case 'svg':
        mimeType = 'image/svg+xml';
        break;
      case 'mp4':
        mimeType = 'video/mp4';
        break;
      case 'mov':
      case 'quicktime':
        mimeType = 'video/quicktime';
        break;
      case 'avi':
        mimeType = 'video/x-msvideo';
        break;
      case 'mkv':
        mimeType = 'video/x-matroska';
        break;
      case 'webm':
        mimeType = 'video/webm';
        break;
    }

    return {
      attachment_id: file.attachment_id,
      fileName: file.original_filename,
      fileUrl: file.preview_url,
      fileSize: 0, // Size not provided in response
      mimeType,
    };
  });
};

// Provider component
export const HotelDetailProvider: React.FC<HotelDetailProviderProps> = ({
  children,
  hotelId,
  skipInitialFetch = false,
}) => {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // Hotel data state
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [hotelLoading, setHotelLoading] = useState(true);
  const [hotelError, setHotelError] = useState<string | null>(null);

  // Subscription state
  const [hotelSubscription, setHotelSubscription] =
    useState<Subscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null
  );

  // Travel facing page state
  const [travelFacingPage, setTravelFacingPage] =
    useState<TravelFacingPage | null>(null);
  const [travelFacingLoading, setTravelFacingLoading] = useState(true);
  const [travelFacingError, setTravelFacingError] = useState<string | null>(
    null
  );

  // ALO Score state (for future use)
  const [aloScore] = useState<AloScore | null>(null);
  const [aloScoreLoading] = useState(false);
  const [aloScoreError] = useState<string | null>(null);

  // Upload loading states
  const [missionStatementLoading, setMissionStatementLoading] = useState(false);
  const [brandLogoUploading, setBrandLogoUploading] = useState(false);
  const [marketingGalleryUploading, setMarketingGalleryUploading] =
    useState(false);
  const [pppGalleryUploading, setPppGalleryUploading] = useState(false);
  const [promotionalVideoUploading, setPromotionalVideoUploading] =
    useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(false);

  // console.log("Hotel Subscription Details ::", hotelSubscription);

  // Fetch hotel data
  const refetchHotel = useCallback(async () => {
    if (!hotelId) return;

    setHotelLoading(true);
    setHotelError(null);

    try {
      // console.log('🔍 Fetching hotel:', { hotelId });
      const response = await apiUtils.get<Hotel>(
        `${API_ENDPOINTS.hotels.list}/${hotelId}`
      );

      // console.log('📡 Hotel API Response:', response);

      if (response && response.data) {
        setHotel(response.data.data);
      } else {
        setHotel(null);
      }
    } catch (err) {
      console.error('❌ Hotel API call failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch hotel';
      setHotelError(errorMessage);
      setHotel(null);
      toastRef.current.error('Failed to load hotel data');
    } finally {
      setHotelLoading(false);
    }
  }, [hotelId]);

  // Silent refetch without loaders - for background updates
  const refetchHotelSilently = useCallback(async () => {
    if (!hotelId) return null;

    try {
      console.log('🔍 Silently fetching hotel:', { hotelId });
      const response = await apiUtils.get<Hotel>(
        `${API_ENDPOINTS.hotels.list}/${hotelId}`
      );

      console.log('📡 Silent Hotel API Response:', response);

      if (response && response.data) {
        const hotelData = response.data.data;
        setHotel(hotelData);
        return hotelData;
      } else {
        return null;
      }
    } catch (err) {
      console.error('❌ Silent hotel API call failed:', err);
      return null;
    }
  }, [hotelId]);

  // Fetch subscription data
  const refetchSubscription = useCallback(async () => {
    if (!hotelId) return;

    setSubscriptionLoading(true);
    setSubscriptionError(null);

    try {
      // console.log('🔍 Fetching hotel subscription:', { hotelId });
      const subscription = await subscriptionService.getHotelSubscription(
        hotelId
      );

      // console.log('📡 Subscription API Response:', subscription);

      if (subscription) {
        setHotelSubscription(subscription);
      } else {
        setHotelSubscription(null);
      }
    } catch (err) {
      console.error('❌ Subscription API call failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch subscription';
      setSubscriptionError(errorMessage);
      setHotelSubscription(null);
      // Don't show error toast as subscription might not exist for all hotels
      console.warn(
        'Subscription fetch failed - hotel may not have subscription'
      );
    } finally {
      setSubscriptionLoading(false);
    }
  }, [hotelId]);

  // Fetch travel facing page data
  const refetchTravelFacing = useCallback(async () => {
    if (!hotelId) return;

    setTravelFacingLoading(true);
    setTravelFacingError(null);

    try {
      // console.log('🔍 Fetching travel facing page:', { hotelId });
      const response = await apiUtils.get<TravelFacingPage>(
        API_ENDPOINTS.travellerFacing.getAll(hotelId)
      );

      // console.log('📡 Travel Facing Page API Response:', response);

      if (response && response.data) {
        setTravelFacingPage(response.data.data);
      } else {
        // Initialize with empty structure if no data
        setTravelFacingPage({
          mission_statement: '',
          brand_logo_attachments: [],
          marketing_gallery_attachments: [],
          ppp_gallery_attachments: [],
          promotional_video_attachments: [],
        });
      }
    } catch (err) {
      console.error('❌ Travel Facing Page API call failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch travel facing page';
      setTravelFacingError(errorMessage);

      // Initialize with empty structure on error
      setTravelFacingPage({
        mission_statement: '',
        brand_logo_attachments: [],
        marketing_gallery_attachments: [],
        ppp_gallery_attachments: [],
        promotional_video_attachments: [],
      });

      toastRef.current.error('Failed to load travel facing page data');
    } finally {
      setTravelFacingLoading(false);
    }
  }, [hotelId]);

  // Update mission statement
  const updateMissionStatement = async (statement: string) => {
    if (!hotelId) return;

    setMissionStatementLoading(true);

    try {
      // console.log('💾 Updating mission statement:', { hotelId, statement });

      await apiUtils.post(
        API_ENDPOINTS.travellerFacing.updateMissionStatement(hotelId),
        { missionStatement: statement }
      );

      // console.log('📡 Mission Statement Update Response:', response);

      // Update local state
      setTravelFacingPage(prev =>
        prev
          ? {
              ...prev,
              mission_statement: statement,
            }
          : {
              mission_statement: statement,
              brand_logo_attachments: [],
              marketing_gallery_attachments: [],
              ppp_gallery_attachments: [],
              promotional_video_attachments: [],
            }
      );

      toastRef.current.success('Mission statement updated successfully');
    } catch (err) {
      console.error('❌ Mission Statement Update failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update mission statement';
      toastRef.current.error(errorMessage);
      throw err;
    } finally {
      setMissionStatementLoading(false);
    }
  };

  // Upload brand logo
  const uploadBrandLogo = async (file: File) => {
    if (!hotelId) return;

    setBrandLogoUploading(true);

    try {
      // console.log('📤 Uploading brand logo:', { hotelId, file: file.name });

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiUtils.post(
        API_ENDPOINTS.travellerFacing.uploadBrandLogo(hotelId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('📡 Brand Logo Upload Response:', response);

      // Update local state with new brand logo
      if (
        response &&
        response.data &&
        response.data.data.uploaded_files &&
        response.data.data.uploaded_files.length > 0
      ) {
        const brandLogoData = convertUploadedFiles(
          response.data.data.uploaded_files
        )[0];

        setTravelFacingPage(prev =>
          prev
            ? {
                ...prev,
                brand_logo_attachments: [brandLogoData],
              }
            : {
                mission_statement: '',
                brand_logo_attachments: [brandLogoData],
                marketing_gallery_attachments: [],
                ppp_gallery_attachments: [],
                promotional_video_attachments: [],
              }
        );
      }

      toastRef.current.success('Brand logo uploaded successfully');
    } catch (err) {
      console.error('❌ Brand Logo Upload failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to upload brand logo';
      toastRef.current.error(errorMessage);
      throw err;
    } finally {
      setBrandLogoUploading(false);
    }
  };

  // Upload marketing gallery
  const uploadMarketingGallery = async (files: File[]) => {
    if (!hotelId || files.length === 0) return;

    setMarketingGalleryUploading(true);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await apiUtils.post(
        API_ENDPOINTS.travellerFacing.uploadMarketingGallery(hotelId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update local state with new gallery items
      if (response && response.data && response.data.data.uploaded_files) {
        const uploadedFiles = convertUploadedFiles(
          response.data.data.uploaded_files
        );

        setTravelFacingPage(prev =>
          prev
            ? {
                ...prev,
                marketing_gallery_attachments: [
                  ...prev.marketing_gallery_attachments,
                  ...uploadedFiles,
                ],
              }
            : {
                mission_statement: '',
                brand_logo_attachments: [],
                marketing_gallery_attachments: uploadedFiles,
                ppp_gallery_attachments: [],
                promotional_video_attachments: [],
              }
        );
      }

      toastRef.current.success(
        `${files.length} file(s) uploaded to property gallery successfully`
      );
    } catch (err) {
      console.error('❌ Property Gallery Upload failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to upload property gallery';
      toastRef.current.error(errorMessage);
      throw err;
    } finally {
      setMarketingGalleryUploading(false);
    }
  };

  // Upload PPP gallery
  const uploadPppGallery = async (files: File[]) => {
    if (!hotelId || files.length === 0) return;

    setPppGalleryUploading(true);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await apiUtils.post(
        API_ENDPOINTS.travellerFacing.uploadPppGallery(hotelId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update local state with new gallery items
      if (response && response.data && response.data.data.uploaded_files) {
        const uploadedFiles = convertUploadedFiles(
          response.data.data.uploaded_files
        );

        setTravelFacingPage(prev =>
          prev
            ? {
                ...prev,
                ppp_gallery_attachments: [
                  ...prev.ppp_gallery_attachments,
                  ...uploadedFiles,
                ],
              }
            : {
                mission_statement: '',
                brand_logo_attachments: [],
                marketing_gallery_attachments: [],
                ppp_gallery_attachments: uploadedFiles,
                promotional_video_attachments: [],
              }
        );
      }

      toastRef.current.success(
        `${files.length} file(s) uploaded to Sustainability gallery successfully`
      );
    } catch (err) {
      console.error('❌ Sustainability Gallery Upload failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to upload Sustainability gallery';
      toastRef.current.error(errorMessage);
      throw err;
    } finally {
      setPppGalleryUploading(false);
    }
  };

  // Upload promotional video
  const uploadPromotionalVideo = async (file: File) => {
    if (!hotelId) return;

    setPromotionalVideoUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiUtils.post(
        API_ENDPOINTS.travellerFacing.uploadPromotionalVideo(hotelId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update local state with new promotional video
      if (
        response &&
        response.data &&
        response.data.data.uploaded_files &&
        response.data.data.uploaded_files.length > 0
      ) {
        const uploadedFiles = convertUploadedFiles(
          response.data.data.uploaded_files
        );

        setTravelFacingPage(prev =>
          prev
            ? {
                ...prev,
                promotional_video_attachments: uploadedFiles,
              }
            : {
                mission_statement: '',
                brand_logo_attachments: [],
                marketing_gallery_attachments: [],
                ppp_gallery_attachments: [],
                promotional_video_attachments: uploadedFiles,
              }
        );
      }

      toastRef.current.success('Promotional video uploaded successfully');
    } catch (err) {
      console.error('❌ Promotional Video Upload failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to upload promotional video';
      toastRef.current.error(errorMessage);
      throw err;
    } finally {
      setPromotionalVideoUploading(false);
    }
  };

  // Delete attachment
  const deleteAttachment = async (attachmentId: string) => {
    console.log('Deleting attachment ::', attachmentId);
    console.log('Current travelfacingpage state ::', travelFacingPage);
    if (!hotelId) return;

    setDeletingAttachment(true);

    try {
      // console.log('🗑️ Deleting attachment:', { hotelId, attachmentId });

      await apiUtils.delete(
        API_ENDPOINTS.travellerFacing.deleteAttachment(hotelId, attachmentId)
      );

      // Update local state by removing the deleted attachment
      setTravelFacingPage(prev => {
        if (!prev) return prev;

        return {
          ...prev,
          brand_logo_attachments: prev.brand_logo_attachments.filter(
            item => item.attachment_id !== attachmentId
          ),
          marketing_gallery_attachments:
            prev.marketing_gallery_attachments.filter(
              item => item.attachment_id !== attachmentId
            ),
          ppp_gallery_attachments: prev.ppp_gallery_attachments.filter(
            item => item.attachment_id !== attachmentId
          ),
          promotional_video_attachments:
            prev.promotional_video_attachments.filter(
              item => item.attachment_id !== attachmentId
            ),
        };
      });

      toastRef.current.success('Attachment deleted successfully');
    } catch (err) {
      console.error('❌ Delete Attachment failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete attachment';
      toastRef.current.error(errorMessage);
      throw err;
    } finally {
      setDeletingAttachment(false);
    }
  };

  // Update hotel submission status locally
  const updateHotelStatus = useCallback((status: string) => {
    setHotel(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        alo_submission_status: status,
      };
    });
  }, []);

  // Update hotel permissions locally
  const updateHotelPermissions = useCallback(
    (permissions: Partial<Hotel['userPermissions']>) => {
      flushSync(() => {
        setHotel(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            userPermissions: {
              ...prev.userPermissions,
              ...permissions,
            },
          };
        });
      });
    },
    []
  );

  const showBoostUpgrade = hotelSubscription?.subscription_code !== 'BOOST';
  const isBoostHotel = hotelSubscription?.subscription_code === 'BOOST';

  // Initial data fetch
  useEffect(() => {
    if (hotelId && !skipInitialFetch) {
      refetchHotel();
      refetchSubscription();
      refetchTravelFacing();
    }
  }, [
    hotelId,
    skipInitialFetch,
    refetchHotel,
    refetchSubscription,
    refetchTravelFacing,
  ]);

  const contextValue: HotelDetailContextState = {
    // Hotel data
    hotel,
    hotelLoading,
    hotelError,

    // Subscription data
    hotelSubscription,
    subscriptionLoading,
    subscriptionError,
    showBoostUpgrade,
    isBoostHotel,

    // Travel facing page data
    travelFacingPage,
    travelFacingLoading,
    travelFacingError,

    // ALO Score data
    aloScore,
    aloScoreLoading,
    aloScoreError,

    // Actions
    refetchHotel,
    refetchHotelSilently,
    refetchSubscription,
    refetchTravelFacing,
    updateMissionStatement,
    uploadBrandLogo,
    uploadMarketingGallery,
    uploadPppGallery,
    uploadPromotionalVideo,
    deleteAttachment,
    updateHotelStatus,
    updateHotelPermissions,

    // Loading states
    missionStatementLoading,
    brandLogoUploading,
    marketingGalleryUploading,
    pppGalleryUploading,
    promotionalVideoUploading,
    deletingAttachment,
  };

  return (
    <HotelDetailContext.Provider value={contextValue}>
      {children}
    </HotelDetailContext.Provider>
  );
};

// Custom hook to use the context
export const useHotelDetail = () => {
  const context = useContext(HotelDetailContext);
  if (!context) {
    throw new Error('useHotelDetail must be used within a HotelDetailProvider');
  }
  return context;
};
